import { App as AntdApp, ConfigProvider, theme as antdTheme, type ThemeConfig } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import { useTheme } from '@/hooks/useTheme';

const antdColorTokensLight = {
  colorPrimary: '#4F19FD',
  colorPrimaryHover: '#6A3DFD',
  colorPrimaryActive: '#3F14CA',
  colorPrimaryBg: '#F8F6FF',
  colorSuccess: '#52A87D',
  colorSuccessHover: '#4A9C72',
  colorSuccessActive: '#3F8C66',
  colorSuccessBg: '#F1F8F4',
  colorWarning: '#FAA118',
  colorWarningHover: '#E29115',
  colorWarningActive: '#C88113',
  colorWarningBg: '#FFF8EE',
  colorError: '#FC1D47',
  colorErrorHover: '#E51A40',
  colorErrorActive: '#C91738',
  colorErrorBg: '#FEF6F8',
  colorInfo: '#2e95c5',
  colorInfoHover: '#2681ac',
  colorInfoActive: '#1d698d',
  colorInfoBg: '#e5f2f9',
  colorLink: '#2E95C5',
  colorLinkHover: '#7AC5DE',
  colorLinkActive: '#1C719E',
  colorBgContainer: '#FFFFFF',
  colorBgElevated: '#FFFFFF',
  colorBgLayout: '#F7F8FA',
  colorText: '#251F2A',
  colorTextSecondary: '#5A575E',
  colorTextTertiary: '#7E7C81',
  colorBorder: '#E4E3E6',
  colorBorderSecondary: '#EBEAED',
} as const;

const antdColorTokensDark = {
  colorPrimary: '#845EFE',
  colorPrimaryHover: '#9F7DFE',
  colorPrimaryActive: '#9F7DFE',
  colorPrimaryBg: '#1F1740',
  colorSuccess: '#3ab37f',
  colorSuccessHover: '#5cc496',
  colorSuccessActive: '#309b6c',
  colorSuccessBg: '#162d20',
  colorWarning: '#e1ac50',
  colorWarningHover: '#e9be76',
  colorWarningActive: '#c89540',
  colorWarningBg: '#362a16',
  colorError: '#e17469',
  colorErrorHover: '#e88e87',
  colorErrorActive: '#c76058',
  colorErrorBg: '#37211e',
  colorInfo: '#4dafda',
  colorInfoHover: '#6ebfe1',
  colorInfoActive: '#3e96bf',
  colorInfoBg: '#162e3a',
  colorLink: '#4DAFDA',
  colorLinkHover: '#6EBFE1',
  colorLinkActive: '#3E96BF',
  colorBgContainer: '#1a1f2a',
  colorBgElevated: '#1a1f2a',
  colorBgLayout: '#11151e',
  colorText: '#e9ebf1',
  colorTextSecondary: '#9da4b2',
  colorTextTertiary: '#9da4b2',
  colorBorder: '#2d3443',
  colorBorderSecondary: '#2d3443',
} as const;

const nonColorTokens = {
  borderRadius: 4,
  borderRadiusSM: 3,
  borderRadiusLG: 6,
  borderRadiusXS: 2,

  fontFamily:
    "'PingFang SC', 'Microsoft YaHei', '微软雅黑', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', 'Segoe UI', sans-serif",
  fontSize: 13,
  fontSizeSM: 12,
  fontSizeLG: 15,

  controlHeight: 32,
  controlHeightSM: 26,
  controlHeightLG: 40,
};

function buildAntdTheme(mode: 'light' | 'dark'): ThemeConfig {
  const isDark = mode === 'dark';
  return {
    cssVar: { key: 'genui' },
    hashed: false,
    algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      ...(isDark ? antdColorTokensDark : antdColorTokensLight),
      ...nonColorTokens,
    },
  };
}

export function AntdConfigProvider({ children }: { children: React.ReactNode }) {
  const { theme } = useTheme();
  return (
    <ConfigProvider locale={zhCN} theme={buildAntdTheme(theme)}>
      <AntdApp>{children}</AntdApp>
    </ConfigProvider>
  );
}
